/*
 * Esprit Salopette — fiche produit (sections/es-pdp.liquid)
 * ------------------------------------------------------------------------------------------------
 * - Sélection des variantes : aucune taille présélectionnée, disponibilités réelles, combinaisons
 *   inexistantes désactivées, variante épuisée = bouton désactivé.
 * - L'ajout au panier reste géré par le <product-form> de Prestige (tiroir, événements, applications).
 * - Barre d'achat mobile qui soumet le même formulaire (même variante, aucun second envoi).
 * - Galerie à défilement natif, vidéo en pause hors écran, agrandissement.
 * - Couleur de la casquette offerte mémorisée pour assets/es-gift.js.
 * - Calculateur de taille : propose, ne sélectionne jamais à la place du client.
 */
(() => {
  const PREFERRED_GIFT_KEY = 'es_gift_variant';

  const storage = {
    get(key) {
      try { return window.localStorage.getItem(key); } catch (error) { return null; }
    },
    set(key, value) {
      try { window.localStorage.setItem(key, value); } catch (error) { /* stockage indisponible */ }
    }
  };

  function formatMoney(cents, format) {
    const value = (Number(cents) || 0) / 100;
    const pattern = format || '{{amount_with_comma_separator}} €';
    const group = (number, decimals, thousands, decimal) => {
      const [integer, fraction] = number.toFixed(decimals).split('.');
      const withThousands = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${withThousands}${decimal}${fraction}` : withThousands;
    };

    return pattern.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
      switch (placeholder) {
        case 'amount': return group(value, 2, ',', '.');
        case 'amount_no_decimals': return group(value, 0, ',', '.');
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        case 'amount_with_space_separator': return group(value, 2, ' ', ',');
        default: return group(value, 2, '.', ',');
      }
    });
  }

  /* ------------------------------------------------------------------------------------------ */
  /* Galerie                                                                                     */
  /* ------------------------------------------------------------------------------------------ */

  class EsGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-es-gallery-track]');
      this.allSlides = Array.from(this.querySelectorAll('[data-es-slide]'));
      this.slides = this.allSlides.filter((slide) => !slide.hidden);
      this.dots = Array.from(this.querySelectorAll('.es-gallery__dot'));
      this.counter = this.querySelector('[data-es-gallery-current]');
      if (!this.track || this.slides.length === 0) return;

      this.current = 0;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const index = this.slides.indexOf(entry.target);
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) this.setCurrent(index);
        });
      }, { root: this.track, threshold: [0, 0.6] });

      this.allSlides.forEach((slide) => this.observer.observe(slide));
      this.setupVideos();

      const prev = this.querySelector('[data-es-gallery-prev]');
      const next = this.querySelector('[data-es-gallery-next]');
      prev && prev.addEventListener('click', () => this.go(this.current - 1));
      next && next.addEventListener('click', () => this.go(this.current + 1));

      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.current + 1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.current - 1); }
      });
    }

    disconnectedCallback() {
      this.observer && this.observer.disconnect();
      this.videoObserver && this.videoObserver.disconnect();
    }

    /*
     * Vidéo : lecture muette automatique quand la diapositive est visible à l'écran (pas de
     * téléchargement avant : preload="none"), pause quand elle sort de la vue ou qu'on change
     * de diapositive. Un bouton permet de mettre en pause ou de relancer ; il sert aussi de
     * solution de repli si le navigateur bloque la lecture automatique. Pas de lecture
     * automatique si l'utilisateur a demandé à réduire les animations.
     */
    setupVideos() {
      this.videos = Array.from(this.querySelectorAll('video[data-es-video]'));
      if (this.videos.length === 0) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      const render = (video) => {
        const button = video.parentElement.querySelector('[data-es-video-toggle]');
        const playing = !video.paused;
        video.parentElement.classList.toggle('is-playing', playing);
        if (!button) return;
        button.setAttribute('aria-pressed', String(playing));
        button.setAttribute('aria-label', playing ? 'Mettre la vidéo en pause' : 'Lire la vidéo');
      };

      const play = (video) => {
        document.querySelectorAll('video[data-es-video]').forEach((other) => { if (other !== video && !other.paused) other.pause(); });
        const attempt = video.play();
        if (attempt && attempt.catch) attempt.catch(() => render(video));
      };

      this.videos.forEach((video) => {
        video.muted = true;
        ['play', 'pause'].forEach((type) => video.addEventListener(type, () => render(video)));
        const button = video.parentElement.querySelector('[data-es-video-toggle]');
        const toggle = () => {
          if (video.paused) {
            delete video.dataset.userPaused;
            play(video);
          } else {
            video.dataset.userPaused = '1';
            video.pause();
          }
        };
        button && button.addEventListener('click', toggle);
        video.addEventListener('click', toggle);
        render(video);
      });

      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector('video[data-es-video]');
          if (!video) return;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            if (!reduceMotion.matches && !video.dataset.userPaused) play(video);
          } else if (!video.paused) {
            video.pause();
          }
        });
      }, { threshold: [0, 0.6] });

      this.videos.forEach((video) => this.videoObserver.observe(video.closest('[data-es-slide]')));
    }

    /*
     * Photos par couleur : n'affiche que les diapositives données, dans l'ordre de la galerie, puis revient à la
     * première. Compteur et points sont recalculés.
     */
    showSlides(slides) {
      if (!this.track) return;
      const visible = this.allSlides.filter((slide) => slides.includes(slide));
      if (visible.length === 0) return;
      this.allSlides.forEach((slide) => { slide.hidden = !visible.includes(slide); });
      this.slides = visible;
      this.slides.forEach((slide, index) => slide.setAttribute('aria-label', String(index + 1)));

      const dots = this.querySelector('.es-gallery__dots');
      if (dots) {
        dots.replaceChildren(...this.slides.map(() => Object.assign(document.createElement('span'), { className: 'es-gallery__dot' })));
        this.dots = Array.from(dots.children);
      }
      const total = this.querySelector('[data-es-gallery-total]');
      if (total) total.textContent = String(this.slides.length);
      const controls = this.querySelector('.es-gallery__controls');
      if (controls) controls.hidden = this.slides.length < 2;
      if (dots) dots.hidden = this.slides.length < 2;

      this.track.scrollTo({ left: 0, behavior: 'auto' });
      this.setCurrent(0);
    }

    setCurrent(index) {
      if (index < 0) return;
      this.current = index;
      this.dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
      if (this.counter) this.counter.textContent = String(index + 1);
    }

    go(index) {
      const target = Math.max(0, Math.min(this.slides.length - 1, index));
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: this.slides[target].offsetLeft - this.track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.setCurrent(target);
    }
  }

  /* ------------------------------------------------------------------------------------------ */
  /* Fiche produit                                                                               */
  /* ------------------------------------------------------------------------------------------ */

  class EsPdp extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-es-pdp-product]');
      if (!json) return;

      this.product = JSON.parse(json.textContent);
      this.moneyFormat = this.dataset.moneyFormat;
      this.form = document.getElementById(this.dataset.formId);
      this.input = this.querySelector('[data-es-variant-input]');
      this.atc = this.querySelector('[data-es-atc]');
      this.atcLabel = this.querySelector('[data-es-atc-label]');
      this.groups = Array.from(this.querySelectorAll('[data-es-option]'));
      this.error = this.querySelector('[data-es-picker-error]');
      this.status = this.querySelector('[data-es-variant-status]');
      this.sticky = this.querySelector('[data-es-sticky]');

      /* Options à valeur unique (ex. Couleur : Denim) : fixées d'office, ce ne sont pas des tailles. */
      this.selection = {};
      this.product.options.forEach((name, index) => {
        const values = new Set(this.product.variants.map((variant) => variant.options[index]));
        if (values.size === 1) this.selection[index] = values.values().next().value;
      });

      this.groups.forEach((group) => {
        const index = Number(group.dataset.esOption);
        const checked = group.querySelector('input:checked');
        if (checked) this.selection[index] = checked.value;
        group.addEventListener('change', (event) => {
          if (!event.target.matches('.es-picker__input')) return;
          this.selection[index] = event.target.value;
          this.hideError();
          this.update(true);
        });
      });

      this.readLabels();
      this.setupColorGroups();
      /* « couleur » quand seule la couleur varie (casquette), « taille » sinon (salopettes) */
      const varying = this.product.options.filter((name, index) => new Set(this.product.variants.map((variant) => variant.options[index])).size > 1);
      this.choiceWord = varying.length > 0 && varying.every((name) => /ouleur|olor/i.test(name)) ? 'couleur' : 'taille';
      this.update(false);
      this.setupFormGuard();
      this.setupSticky();
      this.setupGift();
      this.setupSizeCalculator();
      this.setupLightbox();
    }

    readLabels() {
      this.labels = {
        choose: this.dataset.chooseLabel || 'Choisis ta taille',
        add: this.dataset.atcLabel || 'Ajouter au panier',
        stickyChoose: this.dataset.stickyChooseLabel || 'Choisir ma taille'
      };
    }

    get variant() {
      const count = this.product.options.length;
      for (let index = 0; index < count; index++) {
        if (this.selection[index] === undefined) return null;
      }
      return this.product.variants.find((variant) => variant.options.every((value, index) => value === this.selection[index])) || null;
    }

    missingOptions() {
      return this.product.options.filter((name, index) => this.selection[index] === undefined);
    }

    variantLabel(variant) {
      return variant.options.filter((value, index) => this.groups.some((group) => Number(group.dataset.esOption) === index)).join(' ');
    }

    /* Marque chaque valeur : inexistante (désactivée) ou épuisée (barrée, sélectionnable pour le voir). */
    refreshAvailability() {
      this.groups.forEach((group) => {
        const index = Number(group.dataset.esOption);

        group.querySelectorAll('.es-picker__input').forEach((input) => {
          const matching = this.product.variants.filter((variant) => {
            if (variant.options[index] !== input.value) return false;
            return variant.options.every((value, other) => other === index || this.selection[other] === undefined || this.selection[other] === value);
          });
          const exists = matching.length > 0;
          const available = matching.some((variant) => variant.available);
          const label = group.querySelector(`label[for="${input.id}"]`);

          input.disabled = !exists;
          if (label) {
            label.classList.toggle('is-sold-out', exists && !available);
            label.classList.toggle('is-missing', !exists);
            let hint = label.querySelector('.es-picker__hint');
            if (!hint) {
              hint = document.createElement('span');
              hint.className = 'sr-only es-picker__hint';
              label.appendChild(hint);
            }
            hint.textContent = !exists ? ' (indisponible)' : (!available ? ' (épuisée)' : '');
          }
        });

        const chosen = group.querySelector('[data-es-chosen]');
        if (chosen) chosen.textContent = this.selection[index] || '';
      });
    }

    /*
     * Photos par couleur, deux modes :
     * - « marqueur » : le texte alternatif du média contient « #Couleur_Valeur » (data-es-color) ; une couleur montre
     *   ses médias et ceux sans marqueur (ex. salopette Canvas) ;
     * - « groupes » : chaque variante pointe vers sa première photo (image de variante) ; le groupe d'une couleur va
     *   de cette photo jusqu'à la première photo d'une autre couleur (ex. casquette). Seulement si la galerie ne
     *   contient que des médias produit.
     */
    setupColorGroups() {
      this.colorIndex = this.product.options.findIndex((name) => /ouleur|olor/i.test(name));
      const gallery = this.querySelector('es-gallery');
      const slides = gallery ? Array.from(gallery.querySelectorAll('[data-es-slide]')) : [];
      if (this.colorIndex < 0 || slides.length === 0) return;
      this.galleryColor = this.selection[this.colorIndex];

      if (slides.some((slide) => slide.dataset.esColor)) {
        this.gallery = gallery;
        this.colorSlides = (value) => slides.filter((slide) => !slide.dataset.esColor || slide.dataset.esColor === value.toLowerCase());
        return;
      }

      const order = slides.map((slide) => slide.dataset.esMediaId);
      const starts = new Set(this.product.variants.map((variant) => String(variant.featured_media)).filter((id) => id !== '0' && order.includes(id)));
      if (starts.size < 2 || order.some((id) => !id)) return;
      this.gallery = gallery;
      this.colorSlides = (value) => {
        const variant = this.product.variants.find((item) => item.options[this.colorIndex] === value && starts.has(String(item.featured_media)));
        if (!variant) return [];
        const start = order.indexOf(String(variant.featured_media));
        let end = order.findIndex((id, index) => index > start && starts.has(id));
        if (end < 0) end = order.length;
        return slides.slice(start, end);
      };
    }

    updateGallery() {
      if (!this.gallery || typeof this.gallery.showSlides !== 'function') return;
      const value = this.selection[this.colorIndex];
      if (!value || value === this.galleryColor) return;
      const slides = this.colorSlides(value);
      if (slides.length === 0) return;
      this.galleryColor = value;
      this.gallery.showSlides(slides);
    }

    update(fromUser) {
      this.refreshAvailability();
      if (fromUser) this.updateGallery();
      const variant = this.variant;
      const priceCents = variant ? variant.price : Math.min(...this.product.variants.map((item) => item.price));
      const price = formatMoney(priceCents, this.moneyFormat);

      this.querySelectorAll('[data-es-price], [data-es-bundle-price], [data-es-bundle-total]').forEach((node) => { node.textContent = price; });

      const compare = this.querySelector('[data-es-compare]');
      if (compare) {
        const compareCents = variant ? variant.compare_at_price : 0;
        compare.hidden = !(compareCents > priceCents);
        if (compareCents > priceCents) compare.querySelector('s').textContent = formatMoney(compareCents, this.moneyFormat);
      }

      if (this.input) {
        this.input.value = variant ? variant.id : '';
        this.input.disabled = !variant;
      }

      if (this.atc && this.atcLabel) {
        if (!variant) {
          this.atc.disabled = false;
          this.atc.removeAttribute('aria-disabled');
          this.atcLabel.textContent = this.labels.choose;
        } else if (variant.available) {
          this.atc.disabled = false;
          this.atcLabel.textContent = this.labels.add;
        } else {
          this.atc.disabled = true;
          this.atcLabel.textContent = `Épuisée dans cette ${this.choiceWord}`;
        }
      }

      if (this.status) {
        this.status.textContent = variant
          ? (!variant.available ? `Épuisée dans cette ${this.choiceWord}. Choisis une autre combinaison.` : (variant.backorder ? 'Disponible sur commande : pas en stock immédiat.' : 'En stock.'))
          : '';
        this.status.classList.toggle('is-sold-out', Boolean(variant && !variant.available));
      }

      const bundleSize = this.querySelector('[data-es-bundle-size]');
      if (bundleSize) bundleSize.textContent = variant ? `Taille : ${this.variantLabel(variant)}` : 'Taille : à choisir ci-dessus';

      const stickySize = this.querySelector('[data-es-sticky-size]');
      const stickyButton = this.querySelector('[data-es-sticky-button]');
      if (stickySize) stickySize.textContent = variant ? this.variantLabel(variant) : this.labels.choose;
      if (stickyButton) {
        stickyButton.textContent = !variant ? this.labels.stickyChoose : (variant.available ? this.labels.add : 'Épuisée');
        stickyButton.disabled = Boolean(variant && !variant.available);
      }

      if (fromUser) {
        if (variant) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState(window.history.state, '', url.toString());
        }
        if (this.input) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        if (this.form && variant) {
          this.form.dispatchEvent(new CustomEvent('es:variant-change', { bubbles: true, detail: { variant } }));
        }
      }
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    hideError() {
      if (this.error) this.error.hidden = true;
    }

    focusPicker() {
      const missing = this.groups.find((group) => this.selection[Number(group.dataset.esOption)] === undefined) || this.groups[0];
      if (!missing) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      missing.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      const firstInput = missing.querySelector('.es-picker__input:not(:disabled)');
      if (firstInput) setTimeout(() => firstInput.focus({ preventScroll: true }), reduceMotion ? 0 : 350);
    }

    /* Bloque l'envoi tant que la variante n'est pas choisie (avant le gestionnaire Prestige). */
    setupFormGuard() {
      const productForm = this.form && this.form.closest('product-form');
      if (!productForm) return;

      productForm.addEventListener('submit', (event) => {
        const variant = this.variant;
        if (variant && variant.available) return;

        event.preventDefault();
        event.stopPropagation();

        if (!variant) {
          this.showError(`Choisis d’abord : ${this.missingOptions().join(' et ').toLowerCase()}.`);
          this.focusPicker();
        } else {
          this.showError(`Cette ${this.choiceWord} est épuisée. Choisis une autre combinaison.`);
        }
      }, true);
    }

    setupSticky() {
      if (!this.sticky) return;
      const buyBlock = this.querySelector('.es-buy');
      const button = this.querySelector('[data-es-sticky-button]');
      const mobile = window.matchMedia('(max-width: 999px)');
      if (!buyBlock || !button) return;

      let frame = null;
      const render = () => {
        frame = null;
        const visible = mobile.matches && buyBlock.getBoundingClientRect().bottom < 0;
        if (this.sticky.hidden === !visible) return;
        this.sticky.hidden = !visible;
        document.body.classList.toggle('es-sticky-visible', visible);
      };
      const schedule = () => {
        if (frame === null) frame = requestAnimationFrame(render);
      };

      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      mobile.addEventListener('change', schedule);
      schedule();

      button.addEventListener('click', () => {
        const variant = this.variant;
        if (!variant) {
          this.showError(`Choisis d’abord : ${this.missingOptions().join(' et ').toLowerCase()}.`);
          this.focusPicker();
          return;
        }
        if (!variant.available) return;

        if (this.form.requestSubmit) {
          this.form.requestSubmit(this.atc);
        } else {
          this.atc.click();
        }
      });
    }

    /* Couleur de la casquette offerte. */
    setupGift() {
      const inputs = Array.from(this.querySelectorAll('.es-bundle__input'));
      const nameNode = this.querySelector('[data-es-gift-color-name]');
      const image = this.querySelector('[data-es-gift-image]');
      if (inputs.length === 0) return;

      const apply = (input) => {
        if (!input) return;
        input.checked = true;
        if (nameNode) nameNode.textContent = input.dataset.name;
        if (image && input.dataset.image) {
          image.src = input.dataset.image;
          image.removeAttribute('srcset');
        }
      };

      const savePreferred = (id) => {
        if (window.EsGift) window.EsGift.setPreferred(id);
        else storage.set(PREFERRED_GIFT_KEY, String(id));
      };

      const preferred = storage.get(PREFERRED_GIFT_KEY);
      const initial = inputs.find((input) => input.value === preferred && !input.disabled) || inputs.find((input) => !input.disabled);
      apply(initial);

      inputs.forEach((input) => input.addEventListener('change', () => {
        apply(input);
        savePreferred(input.value);
      }));
    }

    setupSizeCalculator() {
      const calc = this.querySelector('[data-es-size-calc]');
      if (!calc) return;

      const waistInput = calc.querySelector('[data-es-waist]');
      const heightInput = calc.querySelector('[data-es-height]');
      const result = calc.querySelector('[data-es-size-result]');

      const parseTable = (selector) => Array.from(this.querySelectorAll(`${selector} tr`)).map((row) => {
        const label = row.dataset.range || '';
        const numbers = (label.match(/\d+(?:[.,]\d+)?/g) || []).map((value) => Number(value.replace(',', '.')));
        const toCm = (value) => (value < 3 ? Math.round(value * 100) : value);
        const plus = /plus|>|\+/i.test(label);
        const min = numbers.length ? toCm(numbers[0]) : null;
        const max = plus ? Infinity : (numbers.length > 1 ? toCm(numbers[1]) : min);
        return { min, max, size: row.dataset.size };
      }).filter((row) => row.min !== null);

      const waistRows = parseTable('[data-es-waist-table]');
      const lengthRows = parseTable('[data-es-length-table]');

      const findRow = (rows, value) => {
        const rounded = Math.round(value);
        return rows.find((row) => rounded >= row.min && rounded <= row.max)
          || rows.find((row, index) => rows[index + 1] && rounded > row.max && rounded < rows[index + 1].min && rows[index + 1]);
      };

      const optionFor = (size) => this.groups.find((group) => Array.from(group.querySelectorAll('.es-picker__input')).some((input) => input.value === size));

      const offered = (prefix) => {
        const group = this.groups.find((item) => Array.from(item.querySelectorAll('.es-picker__input')).some((input) => input.value.startsWith(prefix)));
        if (!group) return '';
        const values = Array.from(group.querySelectorAll('.es-picker__input')).map((input) => input.value);
        return `${values[0]} à ${values[values.length - 1]}`;
      };

      const line = (text, size, prefix) => {
        const paragraph = document.createElement('p');
        paragraph.className = 'es-size__line';
        paragraph.innerHTML = text;

        if (size) {
          const group = optionFor(size);
          if (group) {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'es-size__pick';
            button.textContent = `Choisir ${size}`;
            button.addEventListener('click', () => {
              const input = Array.from(group.querySelectorAll('.es-picker__input')).find((item) => item.value === size);
              if (input && !input.disabled) {
                input.checked = true;
                input.dispatchEvent(new Event('change', { bubbles: true }));
                input.focus();
              }
            });
            paragraph.appendChild(button);
          } else {
            paragraph.insertAdjacentHTML('beforeend', ` <span class="es-size__warn">${size} n’est pas proposée pour ce modèle (tailles : ${offered(prefix)}).</span>`);
          }
        }

        return paragraph;
      };

      const render = () => {
        result.replaceChildren();
        const waist = parseFloat((waistInput.value || '').replace(',', '.'));
        const height = parseFloat((heightInput.value || '').replace(',', '.'));

        if (waist) {
          const row = findRow(waistRows, waist);
          result.appendChild(row
            ? line(`Tour de taille ${Math.round(waist)} cm → <strong>${row.size}</strong> `, row.size, 'W')
            : line('Ce tour de taille sort du tableau : écris-nous pour un conseil personnalisé.'));
        }

        if (height) {
          const row = findRow(lengthRows, height);
          result.appendChild(row
            ? line(`Repère stature ${Math.round(height)} cm → <strong>${row.size}</strong> (indicatif) `, row.size, 'L')
            : line('Pour cette stature, base-toi sur ton entrejambe ou écris-nous.'));
        }

        if (waist || height) {
          result.appendChild(line('<small>Correspondance indicative : ton entrejambe et les mesures d’un pantalon qui te va priment.</small>'));
        }
      };

      waistInput.addEventListener('input', render);
      heightInput.addEventListener('input', render);
    }

    setupLightbox() {
      const dialog = this.querySelector('[data-es-lightbox]');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      const image = dialog.querySelector('[data-es-lightbox-img]');

      this.querySelectorAll('[data-es-zoom]').forEach((img) => {
        img.style.cursor = 'zoom-in';
        img.addEventListener('click', () => {
          const sources = (img.getAttribute('srcset') || '').split(',').map((part) => part.trim().split(' ')[0]).filter(Boolean);
          image.src = sources[sources.length - 1] || img.currentSrc || img.src;
          image.alt = img.alt;
          dialog.showModal();
        });
      });

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog || event.target.closest('[data-es-lightbox-close]')) dialog.close();
      });
    }
  }

  /* Un lien vers l'aide à la taille (#es-size-guide) ouvre le panneau. */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href="#es-size-guide"]');
    const guide = document.getElementById('es-size-guide');
    if (link && guide) guide.open = true;
  });

  if (!customElements.get('es-gallery')) customElements.define('es-gallery', EsGallery);
  if (!customElements.get('es-pdp')) customElements.define('es-pdp', EsPdp);
})();
